/**
 * UCC Jurisdictions Hooks
 *
 * React Query hooks for jurisdiction rules and fee calculation.
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import type { components } from '@aaimplatform/institutional-types/ucc-jurisdiction-rules';
import { uccJurisdictionRulesClient } from '../api/typed-clients';

/** Request body of POST /jurisdictions/{stateCode}/calculate-fee. */
export type FeeCalculationRequest = components['schemas']['FeeCalculationRequest'];
import { apiErrorMessage } from '../api/error-message';

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const errorBody = result.error as { message?: string } | undefined;
    throw new Error(apiErrorMessage(errorBody) ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

export function useJurisdictions() {
  return useQuery({
    queryKey: ['uccJurisdictions'],
    queryFn: async () => {
      const result = await uccJurisdictionRulesClient.GET('/jurisdictions');
      return unwrap(result);
    },
  });
}

export function useJurisdiction(stateCode: string | undefined) {
  return useQuery({
    queryKey: ['uccJurisdiction', stateCode],
    queryFn: async () => {
      const result = await uccJurisdictionRulesClient.GET('/jurisdictions/{stateCode}', {
        params: { path: { stateCode: stateCode! } },
      });
      return unwrap(result);
    },
    enabled: !!stateCode,
  });
}

export function useJurisdictionFeeSchedule(stateCode: string | undefined) {
  return useQuery({
    queryKey: ['uccJurisdictionFees', stateCode],
    queryFn: async () => {
      const result = await uccJurisdictionRulesClient.GET('/jurisdictions/{stateCode}/fee-schedule', {
        params: { path: { stateCode: stateCode! } },
      });
      return unwrap(result);
    },
    enabled: !!stateCode,
  });
}

export function useCalculateFee() {
  return useMutation({
    mutationFn: async ({ stateCode, body }: { stateCode: string; body: FeeCalculationRequest }) => {
      const result = await uccJurisdictionRulesClient.POST('/jurisdictions/{stateCode}/calculate-fee', {
        params: { path: { stateCode } },
        body,
      });
      return unwrap(result);
    },
  });
}
